import { NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";
import { AuthGuard } from "./shared/auth/auth.guard";
import { RedirectIfAuthenticatedGuard } from "./shared/auth/redirect-if-authenticated.guard";
import { RootLayoutComponent } from "./layouts/root-layout/root-layout.component";
import { PublicLayoutComponent } from "./layouts/public/public-layout/public-layout.component";
import { SiteGuard } from "./shared/auth/site.guard";
import { TestComponent } from "./study/test/test.component";

const routes: Routes = [
  {
    path: "",
    component: RootLayoutComponent,
    children: [
      {
        path: "dashboard",
        loadChildren: () =>
          import("./dashboard/dashboard.module").then(
            (m) => m.DashhboardModule,
          ),
        canActivate: [AuthGuard],
      },
      {
        path: "passes",
        loadChildren: () =>
          import("./pass/pass.module").then((m) => m.PassModule),
        canActivate: [AuthGuard],
      },
      {
        path: "inbox",
        loadChildren: () =>
          import("./inbox/inbox.module").then((m) => m.InboxModule),
        canActivate: [AuthGuard],
      },
      {
        path: "locators",
        loadChildren: () =>
          import("./locator/locator.module").then((m) => m.LocatorModule),
        canActivate: [AuthGuard],
      },
      {
        path: "vehicles",
        loadChildren: () =>
          import("./vehicle/vehicle.module").then((m) => m.VehicleModule),
        canActivate: [AuthGuard],
      },
      {
        path: "users",
        loadChildren: () =>
          import("./user/user.module").then((m) => m.UserModule),
        canActivate: [AuthGuard],
      },
      {
        path: "roles",
        loadChildren: () =>
          import("./role/role.module").then((m) => m.RoleModule),
        canActivate: [AuthGuard],
      },
      {
        path: "invitations",
        loadComponent: () =>
          import("./invitation/invitation-list.component").then(
            (m) => m.InvitationListComponent,
          ),
        canActivate: [AuthGuard],
      },
      {
        path: "stickers",
        loadChildren: () =>
          import("./sticker/sticker.routes").then((m) => m.STICKER_ROUTES),
        canActivate: [AuthGuard],
      },
      {
        path: "messages",
        loadChildren: () =>
          import("./message/message.routes").then((m) => m.MESSAGE_ROUTES),
        canActivate: [AuthGuard],
      },
      {
        path: "permits",
        loadChildren: () =>
          import("./permit/permit.routes").then((m) => m.PERMIT_ROUTES),
        canActivate: [AuthGuard],
      },
      {
        path: "village",
        loadChildren: () =>
          import("./village/village.routes").then((m) => m.VILLAGE_ROUTES),
        canActivate: [AuthGuard],
      },
      {
        path: "reports/activity",
        loadChildren: () =>
          import("./reports/activity/activity-report.module").then(
            (m) => m.ActivityReportModule,
          ),
        canActivate: [AuthGuard],
      },
      {
        path: "reports/gate-log",
        loadChildren: () =>
          import("./reports/gate-log/gate-log-report.module").then(
            (m) => m.GateLogReportModule,
          ),
        canActivate: [AuthGuard],
      },
      {
        path: "myaccount",
        canActivate: [AuthGuard],
        children: [
          // Layout routes (with sidebar)
          {
            path: "",
            loadComponent: () =>
              import("./myaccount/myaccount-layout/myaccount-layout.component").then(
                (m) => m.MyAccountLayoutComponent,
              ),
            children: [
              { path: "", redirectTo: "profile", pathMatch: "full" },
              {
                path: "profile",
                loadComponent: () =>
                  import("./myaccount/profile-section/profile-section.component").then(
                    (m) => m.ProfileSectionComponent,
                  ),
              },
              {
                path: "security",
                loadComponent: () =>
                  import("./myaccount/security-section/security-section.component").then(
                    (m) => m.SecuritySectionComponent,
                  ),
              },
              {
                path: "sites",
                loadComponent: () =>
                  import("./myaccount/sites-section/sites-section-settings.component").then(
                    (m) => m.SitesSectionSettingsComponent,
                  ),
              },
              {
                path: "notifications",
                loadComponent: () =>
                  import("./myaccount/notifications-section/notifications-section.component").then(
                    (m) => m.NotificationsSectionComponent,
                  ),
              },
            ],
          },
          // Sites list (standalone, no sidebar)
          {
            path: "sites/list",
            loadComponent: () =>
              import("./myaccount/sites-list/sites-list.component").then(
                (m) => m.SitesListComponent,
              ),
          },
          // Edit routes (standalone, no sidebar)
          {
            path: "edit/name",
            loadComponent: () =>
              import("./myaccount/edit/name-edit/name-edit.component").then(
                (m) => m.NameEditComponent,
              ),
          },
          {
            path: "edit/display-name",
            loadComponent: () =>
              import("./myaccount/edit/display-name-edit/display-name-edit.component").then(
                (m) => m.DisplayNameEditComponent,
              ),
          },
          {
            path: "edit/picture",
            loadComponent: () =>
              import("./myaccount/edit/picture-edit/picture-edit.component").then(
                (m) => m.PictureEditComponent,
              ),
          },
          {
            path: "edit/username",
            loadComponent: () =>
              import("./myaccount/edit/username-edit/username-edit.component").then(
                (m) => m.UsernameEditComponent,
              ),
          },
          {
            path: "edit/password",
            loadComponent: () =>
              import("./myaccount/edit/password-edit/password-edit.component").then(
                (m) => m.PasswordEditComponent,
              ),
          },
          {
            path: "edit/email",
            loadComponent: () =>
              import("./myaccount/edit/email-edit/email-edit.component").then(
                (m) => m.EmailEditComponent,
              ),
          },
          {
            path: "edit/mobile",
            loadComponent: () =>
              import("./myaccount/edit/mobile-edit/mobile-edit.component").then(
                (m) => m.MobileEditComponent,
              ),
          },
        ],
      },
      // load test component that staight out
      //
      // {
      //   path: "",
      //   component: TestComponent,
      //   canActivate: [AuthGuard],
      // },
      {
        path: "",
        loadChildren: () =>
          import("./home/home.module").then((m) => m.HomeModule),
        canActivate: [AuthGuard],
      },
      {
        path: "site-directory",
        loadComponent: () =>
          import("./site-directory/site-directory.component").then(
            (m) => m.SiteDirectoryComponent,
          ),
        canActivate: [AuthGuard],
      },
      {
        path: "site-select",
        loadComponent: () =>
          import("./site-select/site-select.component").then(
            (m) => m.SiteSelectComponent,
          ),
        canActivate: [AuthGuard],
      },
    ],
  },
  {
    path: "",
    component: PublicLayoutComponent,
    children: [
      {
        path: "privacy",
        loadChildren: () =>
          import("./privacy/privacy.module").then((m) => m.PrivacyModule),
      },
      {
        path: "terms",
        loadChildren: () =>
          import("./terms/terms.module").then((m) => m.TermsModule),
      },
    ],
  },
  {
    path: "login",
    loadChildren: () =>
      import("./login/login.module").then((m) => m.LoginModule),
    canActivate: [RedirectIfAuthenticatedGuard],
  },
  {
    path: "onboarding",
    loadChildren: () =>
      import("./onboarding/onboarding.module").then((m) => m.OnboardingModule),
  },
  {
    path: "forgot-password",
    loadChildren: () =>
      import("./forgot-password/forgot-password.module").then(
        (m) => m.ForgotPasswordModule
      ),
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { enableTracing: false })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
